import {
  Component,
  inject,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { PAGE_CONSTANTS } from '../../../../shared/constants/page.constants';
import { STATIC_DATA, STATIC_JSON_DATA_PATH } from '../../../../shared/constants/static-data.constants';
import { ListTableComponent } from '../../../../shared/components/list-table/list-table.component';
import { PageConfig } from '../../../../shared/authorization/auth.config';
import { Router } from '@angular/router';
import { ClaimsService } from '../../../../services/claims.service';
import { SectionsService } from '../../../../services/sections.service';
import { AuthorizationUtility } from '../../../../shared/authorization/auth.utility';
import { AuthorizationService } from '../../../../shared/authorization/authorization.service';
import { SharedDataService } from '../../../../shared/services/shared-data-service';
import { BillingService } from '../../../../services/billing.service';
import {
  DialogConfig,
  DialogService,
} from '../../../../shared/services/dialog.service';
import { BillingFilterComponent } from '../../billing-filter/billing-filter.component';
import { ListService } from '../../../../shared/services/list.service';
import { IgnoredTimesheetsComponent } from '../../ignored-timesheets/ignored-timesheets/ignored-timesheets.component';
import { UserService } from '../../../../services/user.service';
import { LoggerService } from '../../../../shared/services/logger.service';
import { LookupService } from '../../../../services/lookup.service';
import { AccordionComponent } from '../../../../shared/components/accordion/accordion.component';
import { DropdownSelectComponent } from '../../../../shared/components/dropdown-select/dropdown-select.component';

@Component({
  selector: 'ppl-consumer-ledger-list',
  standalone: true,
  imports: [AccordionComponent, IgnoredTimesheetsComponent, ListTableComponent, DropdownSelectComponent, FormsModule, NzInputModule, NzDatePickerModule],
  templateUrl: './patient-ledger-list.component.html',
  styleUrl: './patient-ledger-list.component.css',
})
export class PatientLedgerListComponent implements OnInit {
  sharedDataKey: string = STATIC_DATA.SHARED_BATCHES_DATA;
  pageId: string = PAGE_CONSTANTS.BATCHES_GRID_PAGE;
  programName!: string;
  lazyLoad = true;

  cols = [
    { field: 'pplIcn', header: 'PPL ICN' },
    { field: 'dateOfService', header: 'Date of Service' },
    { field: 'claimCreationDate', header: 'Claim Creation Date' },
    { field: 'mcoBilled', header: 'MCO Billed' },
    { field: 'timesheets', header: 'Timesheets' },
    { field: 'units', header: 'Units' },
    { field: 'serviceCode', header: 'Service Code' },
    { field: 'ratePerUnit', header: 'Rate / Unit' },
    { field: 'totalCharges', header: 'Total Charges' },
    { field: 'payments', header: 'Payments' },
    { field: 'arAmount', header: 'AR Amount' },
    { field: 'status', header: 'Status' },
  ];

  lineItemCols = [
    { field: 'timesheetId', header: 'Timesheet ID' },
    { field: 'authorization', header: 'Authorization' },
    { field: 'serviceDate', header: 'Service Date' },
    { field: 'serviceCode', header: 'Service Code' },
    { field: 'serviceDuration', header: 'Service Duration' },
    { field: 'serviceUnits', header: 'Service Units' },
    { field: 'charges', header: 'Charges' },
    { field: 'processingStatus', header: 'Processing Status' },
  ];

  claims: any[] = [];

  participant: any = null;

  searchPplId = '';

  private searchedParticipantPplId = '';

  searchPplIcn = '';

  filterMco = '';
  filterStartDate: Date | null = null;
  filterEndDate: Date | null = null;
  mcoOptions: string[] = [];

  constructor(
    private router: Router,
    private sharedDataService: SharedDataService,
    private sectionService: SectionsService,
    private claimsService: ClaimsService,
    private billingService: BillingService,
    private listService: ListService,
    private userService: UserService,
    private logger: LoggerService,
    private lookupService: LookupService
  ) {}

  ngOnInit(): void {
    const selectedProgram = this.userService.getSelectedProgram();
    this.programName = selectedProgram?.program_code ?? '';
    this.loadMcoOptions();
  }

  private loadMcoOptions(): void {
    this.lookupService.getOrgData(this.programName || 'NY').subscribe({
      next: (response: any) => {
        const orgs = response?.responsedata ?? [];
        this.mcoOptions = orgs
          .filter((o: any) => o.orgTypeLookup === 'MCO')
          .map((o: any) => o.orgName)
          .sort();
      },
      error: (err: any) => {
        this.logger.error('Failed to load MCO org list', err);
      },
    });
  }

  rowClicked(event: any): void {
    if (event) {
      event.showLineItem = !event.showLineItem;
    }
  }

  onSearch(): void {
    const pplId = this.searchPplId.trim();
    if (!pplId) {
      return;
    }
    this.searchPplIcn = '';
    this.filterMco = '';
    this.filterStartDate = null;
    this.filterEndDate = null;
    this.fetchConsumerLedger(pplId);
  }

  onFiltersChange(): void {
    if (this.searchPplIcn.trim() && this.searchedParticipantPplId) {
      this.fetchConsumerLedger(this.searchedParticipantPplId, this.searchPplIcn.trim());
    } else if (this.searchedParticipantPplId || this.searchPplId.trim()) {
      this.fetchConsumerLedger(this.searchedParticipantPplId || this.searchPplId.trim());
    }
  }

  private buildDateRangeFilters(): { mcoName?: string; startDate?: string; endDate?: string } {
    const filters: { mcoName?: string; startDate?: string; endDate?: string } = {};
    if (this.filterMco.trim()) {
      filters.mcoName = this.filterMco.trim();
    }
    if (this.filterStartDate) {
      filters.startDate = this.toIsoDate(this.filterStartDate);
    }
    if (this.filterEndDate) {
      filters.endDate = this.toIsoDate(this.filterEndDate);
    }
    return filters;
  }

  private toIsoDate(date: Date): string {
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
  }

  private fetchConsumerLedger(participantPplId: string, pplIcn?: string): void {
    const filters = this.buildDateRangeFilters();
    this.billingService
      .getConsumerLedgerList(this.programName || 'NY', participantPplId, pplIcn, filters)
      .subscribe({
        next: (response: any) => {
          const consumerLedger = response?.responsedata?.consumerLedger;
          const claims: any[] = consumerLedger?.claims ?? [];
          this.claims = claims.map((claim) => this.buildClaimRow(claim));
          this.participant = this.claims.length ? this.buildParticipantView(consumerLedger ?? {}) : null;
          if (this.participant) {
            this.searchedParticipantPplId = participantPplId;
          }
        },
        error: (err) => {
          this.logger.error('Failed to load consumer ledger', err);
          this.claims = [];
          this.participant = null;
        },
      });
  }

  private buildParticipantView(c: any) {
    return {
      participantId: c?.participantId ?? '-',
      participantPplId: c?.participantPplId || '-',
      fullName: c?.participantFullName || '-',
      dateOfBirth: c?.participantDateOfBirth ? this.formatDate(c.participantDateOfBirth) : '-',
      gender: c?.participantGender || '-',
    };
  }

  onClaimClick(payload: any): void {
    const claimId = payload?.event?.claimId;
    if (claimId == null) {
      return;
    }
    const claimFamilyId = payload?.event?.claimFamilyId;
    this.router.navigate(['/home/claimLedgerList'], {
      state: { claimId, claimFamilyId },
    });
  }

  private buildClaimRow(claim: any) {
    const timesheetCount = claim.claimTimesheets?.length ?? 0;
    const arAmountRaw = Number(claim.claimBilledAmount) - Number(claim.claimPaymentAmount);
    const arAmount = Math.abs(arAmountRaw) < 1e-9 ? 0 : arAmountRaw;
    const status = this.paymentStatusLabel(claim.claimPaymentStatus);

    return {
      claimId: claim.claimID,
      claimFamilyId: claim.claimFamilyId,
      dateOfService: this.formatDate(claim.claimServiceDate),
      claimCreationDate: this.formatDate(claim.claimCreationDate),
      pplIcn: claim.claimPplIcn,
      mcoBilled: claim.claimMcoName,
      timesheets: `${timesheetCount} ${timesheetCount === 1 ? 'timesheet' : 'timesheets'}`,
      units: Number(claim.claimUnits).toFixed(1),
      serviceCode: claim.claimServiceCode,
      ratePerUnit: this.formatCurrency(claim.claimBillRate),
      totalCharges: this.formatCurrency(claim.claimBilledAmount),
      payments: this.formatCurrency(claim.claimPaymentAmount),
      arAmount: this.formatArAmount(arAmount),
      arAmountClass: arAmount > 0 ? 'positive' : 'zero',
      status,
      statusClass: this.statusPillClass(status),
      showLineItem: false,
      lineItems: (claim.claimTimesheets ?? []).map((ts: any) => ({
        timesheetId: ts?.timesheetID ?? '-',
        authorization: claim.claimAuthNumber ?? '-',
        serviceDate: this.formatDate(ts?.serviceDate),
        serviceCode: ts?.serviceCode ?? '-',
        serviceDuration: ts?.serviceDuration ?? '-',
        serviceUnits: ts?.serviceUnits != null ? Number(ts.serviceUnits).toFixed(1) : '-',
        charges: ts?.charges != null ? this.formatCurrency(ts.charges) : '-',
        processingStatus: this.timesheetStatusLabels[ts?.timesheetProcessingStatus] ?? ts?.timesheetProcessingStatus ?? '-',
      })),
    };
  }

  private readonly timesheetStatusLabels: Record<string, string> = {
    P: 'Pending',
    RP: 'Ready to Process',
    RV: 'In Review',
  };

  private paymentStatusLabel(status: string): string {
    if (!status) {
      return '-';
    }
    return status
      .toLowerCase()
      .split(' ')
      .map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1) : w))
      .join(' ');
  }

  private statusPillClass(status: string): string {
    const s = (status || '').toUpperCase();
    if (s.includes('PARTIAL')) {
      return 'partial';
    }
    if (s.includes('DENY') || s.includes('DENIED')) {
      return 'denied';
    }
    if (s.includes('RESUBMIT')) {
      return 'resubmitted';
    }
    if (s.includes('RETRACT') || s.includes('PENDING')) {
      return 'pending';
    }
    if (s.includes('PAID')) {
      return 'paid';
    }
    return 'default';
  }

  private formatDate(iso: string): string {
    if (!iso) {
      return '-';
    }
    const [year, month, day] = iso.split('-');
    return `${month}/${day}/${year}`;
  }

  private formatCurrency(value: number): string {
    const num = Number(value) || 0;
    const sign = num < 0 ? '-' : '';
    return `${sign}$${Math.abs(num).toFixed(2)}`;
  }

  private formatArAmount(value: number): string {
    const num = Number(value) || 0;
    const sign = num < 0 ? '-' : '';
    const abs = Math.abs(num);
    const decimals = abs > 0 && Math.round(abs * 100) / 100 === 0 ? 4 : 2;
    return `${sign}$${abs.toFixed(decimals)}`;
  }
}
